import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
    XMarkIcon,
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    IdentificationIcon,
    CalendarIcon,
    CheckCircleIcon,
    XCircleIcon,
    LockClosedIcon,
    LockOpenIcon,
} from '@heroicons/react/24/outline'
import { colorMap, formatDateAnTime } from '../../../../Common'

const getInitials = (nome) => {
    if (!nome) return '?'
    return nome.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
}

const DataField = ({ icon: Icon, label, value }) => (
    <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-gray-500" />
                {label}
            </div>
        </label>
        <p className="mt-1 block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 shadow-sm">
            {value || '—'}
        </p>
    </div>
)

const VisualizacaoInvestidor = ({ states, setters, color = 'yellow' }) => {
    const colorClasses = colorMap[color] || colorMap.yellow
    const inv = states.Investidores || {}

    return (
        <Transition.Root show={states.modalViewInvestidores} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setters.setModalViewInvestidores(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
                    leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">

                                {/* Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className={`flex h-12 w-12 items-center justify-center rounded-full ${colorClasses.headerIconBg} flex-shrink-0`}>
                                            <span className={`text-lg font-bold ${colorClasses.headerIconText}`}>
                                                {getInitials(inv.nome)}
                                            </span>
                                        </div>
                                        <div>
                                            <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900">
                                                {inv.nome || 'Investidor'}
                                            </Dialog.Title>
                                            <p className="text-sm text-gray-500 mt-1">{inv.email}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setters.setModalViewInvestidores(false)}
                                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
                                    >
                                        <XMarkIcon className="w-6 h-6" />
                                    </button>
                                </div>

                                {/* Badges de status */}
                                <div className="flex items-center gap-3 mb-6">
                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold ${
                                        inv.bloqueado ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
                                    }`}>
                                        {inv.bloqueado
                                            ? <><LockClosedIcon className="w-3.5 h-3.5" /> Bloqueado</>
                                            : <><LockOpenIcon className="w-3.5 h-3.5" /> Ativo</>}
                                    </span>
                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold ${
                                        inv.email_verificado ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
                                    }`}>
                                        {inv.email_verificado
                                            ? <><CheckCircleIcon className="w-3.5 h-3.5" /> E-mail verificado</>
                                            : <><XCircleIcon className="w-3.5 h-3.5" /> E-mail pendente</>}
                                    </span>
                                </div>

                                {/* Dados */}
                                <div className="space-y-5">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <DataField icon={UserIcon} label="Nome completo" value={inv.nome} />
                                        <DataField icon={EnvelopeIcon} label="E-mail" value={inv.email} />
                                        <DataField icon={IdentificationIcon} label="CPF / CNPJ" value={inv.cpf_cnpj} />
                                        <DataField icon={PhoneIcon} label="Telefone" value={inv.telefone} />
                                    </div>

                                    <div className="border-t border-gray-100 pt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <DataField icon={CalendarIcon} label="Criado em" value={formatDateAnTime(inv.data_criacao)} />
                                        <DataField icon={CalendarIcon} label="Atualizado em" value={formatDateAnTime(inv.data_atualizacao)} />
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="mt-8 flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => setters.setModalViewInvestidores(false)}
                                        className="px-6 py-2.5 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all duration-200"
                                    >
                                        Fechar
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default VisualizacaoInvestidor
